import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SkillLevelCategoryTag } from './SkillLevelCategoryTag';
import { cn, getSkillLevelName } from '@/lib/utils';
import { SkillLevel, SkillLevelCategory } from '@sportschool/shared-types';
import { useSkillLevels, useAuthStore } from '@sportschool/shared-logic';
import { getCachedSkillLevels, setCachedSkillLevels, getSkillLevelHexColor } from '@/lib/skillLevelCache';

export interface SkillLevelBadgeProps {
  levelId?: string | null;
  levelName?: string | null;
  skillLevels?: SkillLevel[] | null;
  schoolId?: string | null;
  className?: string;
  showWarningIfMissing?: boolean;
  fallbackText?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const SkillLevelBadge: React.FC<SkillLevelBadgeProps> = ({
  levelId,
  levelName: propLevelName,
  skillLevels: passedSkillLevels,
  schoolId: propSchoolId,
  className,
  showWarningIfMissing = false,
  fallbackText,
  size = 'md',
}) => {
  const { t, i18n } = useTranslation();
  const routeParams = useParams<{ schoolId?: string }>();
  const activeSchool = useAuthStore((s) => s.userContext?.activeSchool);

  // Auto-resolve schoolId from props, route params, active school context, or localStorage
  const schoolId = React.useMemo(() => {
    return propSchoolId || routeParams.schoolId || activeSchool?.id || localStorage.getItem('active_school_id') || null;
  }, [propSchoolId, routeParams.schoolId, activeSchool?.id]);

  // Resolve levelName from props
  const levelName = propLevelName ?? null;

  // Synchronously load cached levels from LocalStorage if available
  const cachedLevels = React.useMemo(() => {
    return getCachedSkillLevels(schoolId);
  }, [schoolId]);

  // Automatically query skill levels if not passed and not in cache
  const shouldQuery = Boolean(schoolId && !passedSkillLevels && !cachedLevels);
  const { data: queriedLevels } = useSkillLevels(shouldQuery ? schoolId! : '');

  // Update LocalStorage cache whenever fresh levels are queried
  React.useEffect(() => {
    if (schoolId && queriedLevels && queriedLevels.length > 0) {
      setCachedSkillLevels(schoolId, queriedLevels);
    }
  }, [schoolId, queriedLevels]);

  const effectiveSkillLevels = React.useMemo(() => {
    return passedSkillLevels || cachedLevels || queriedLevels || [];
  }, [passedSkillLevels, cachedLevels, queriedLevels]);

  // Match target skill level
  const matchedLevel = React.useMemo(() => {
    if (levelId) {
      const found = effectiveSkillLevels.find((l) => l.id === levelId);
      if (found) return found;
    }
    if (levelName) {
      const found = effectiveSkillLevels.find((l) => l.name === levelName || l.namePl === levelName || l.nameEn === levelName);
      if (found) return found;
    }
    return null;
  }, [effectiveSkillLevels, levelId, levelName]);

  const displayName = getSkillLevelName(
    matchedLevel || { name: levelName || undefined },
    i18n.language
  ) || fallbackText || t('users.fields.noSkillLevel', 'Brak poziomu');

  const hexColor = getSkillLevelHexColor(matchedLevel?.color);

  const hasLevel = Boolean(matchedLevel || levelName);

  const sizeClasses = {
    sm: 'text-[9px] px-1.5 py-0.5 rounded-md font-semibold',
    md: 'text-[10px] px-2 py-0.5 rounded-md font-bold',
    lg: 'text-xs px-2.5 py-1 rounded-lg font-bold',
  }[size];

  if (!hasLevel && showWarningIfMissing) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground text-xs italic">
        <span>{displayName}</span>
        <span title={t('sessions.details.attendanceListLevelWarning', 'Nie podano poziomu zaawansowania')}>
          <AlertTriangle className="w-4 h-4 text-warning shrink-0 cursor-help animate-pulse" />
        </span>
      </span>
    );
  }

  if (!hasLevel) {
    return (
      <span className={cn('text-muted-foreground italic text-xs', className)}>
        {displayName}
      </span>
    );
  }

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'border inline-flex items-center justify-center tracking-wide transition-colors',
        sizeClasses,
        className
      )}
      style={{
        backgroundColor: hexColor ? `${hexColor}18` : undefined,
        borderColor: hexColor ? `${hexColor}40` : undefined,
        color: hexColor || undefined,
      }}
    >
      {displayName}
    </Badge>
  );

  // Only tag levels resolved by id: a name alone cannot tell apart same-named levels from different categories.
  const category = levelId && matchedLevel?.id === levelId ? matchedLevel.category : undefined;
  if (!category || category === SkillLevelCategory.Adults) return badge;

  return (
    <span className="inline-flex items-center gap-1">
      {badge}
      <SkillLevelCategoryTag category={category} />
    </span>
  );
};

export default SkillLevelBadge;
